import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import api from "../../lib/api";
import PageHeader from "../../components/PageHeader";
import { Button } from "../../components/ui/button";
import CrudModal from "../../components/CrudModal";
import { toast } from "sonner";
import { Exam, FileText, Stamp, PencilSimple, Trash, Plus } from "@phosphor-icons/react";

const STATUS_CLS = {
  Scheduled: "bg-secondary text-foreground",
  Evaluation: "bg-amber-50 text-amber-700",
  Published: "bg-emerald-50 text-emerald-700",
};
const MODE_CLS = {
  Online: "bg-indigo-50 text-indigo-700",
  OMR: "bg-secondary text-foreground",
  "Pen-Paper": "bg-secondary text-foreground",
};

const FIELDS = [
  { name: "code", label: "Code", type: "text" },
  { name: "title", label: "Title", type: "text" },
  { name: "date", label: "Date (YYYY-MM-DD)", type: "text" },
  { name: "mode", label: "Mode", type: "select", options: [
    { value: "Online", label: "Online" }, { value: "OMR", label: "OMR" }, { value: "Pen-Paper", label: "Pen-Paper" },
  ]},
  { name: "students", label: "# Students", type: "number" },
  { name: "status", label: "Status", type: "select", options: [
    { value: "Scheduled", label: "Scheduled" }, { value: "Evaluation", label: "Evaluation" }, { value: "Published", label: "Published" },
  ]},
];

export default function Exams() {
  const { t } = useTranslation();
  const [exams, setExams] = useState([]);
  const [modal, setModal] = useState({ open: false, editing: null });

  const load = useCallback(async () => {
    const { data } = await api.get("/exams");
    setExams(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openCreate = () => setModal({ open: true, editing: null });
  const openEdit = (e) => setModal({ open: true, editing: e });
  const close = () => setModal({ open: false, editing: null });

  const save = async (values) => {
    try {
      if (modal.editing) {
        await api.patch(`/exams/${modal.editing.exam_id}`, values);
        toast.success("Exam updated");
      } else {
        await api.post("/exams", values);
        toast.success("Exam created");
      }
      close(); load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    }
  };

  const remove = async (e) => {
    if (!window.confirm(`Delete exam ${e.code} — ${e.title}?`)) return;
    try {
      await api.delete(`/exams/${e.exam_id}`);
      toast.success("Exam deleted"); load();
    } catch { toast.error("Failed to delete"); }
  };

  return (
    <div>
      <PageHeader
        overline="M10 · Assessment"
        title={t("modules.exams.title")}
        description={t("modules.exams.subtitle")}
        action={
          <Button onClick={openCreate} className="rounded-md bg-[hsl(244_53%_20%)] hover:bg-[hsl(244_53%_15%)]" data-testid="new-exam-btn">
            <Plus size={14} className="mr-1.5"/> Create exam
          </Button>
        }
      />
      <div className="p-6 lg:p-8 space-y-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-border border border-border rounded-md overflow-hidden">
          {[
            { l: "Question bank", v: "12,840", t: "Bloom-tagged" },
            { l: "Scheduled exams", v: exams.filter(e => e.status === "Scheduled").length, t: "next 30 days" },
            { l: "Papers evaluated", v: "84%", t: "on-screen" },
            { l: "Avg. score", v: "72/100", t: "this term" },
          ].map(s => (
            <div key={s.l} className="bg-white p-5">
              <div className="eos-overline">{s.l}</div>
              <div className="font-heading font-extrabold text-3xl mt-2 tracking-tight">{s.v}</div>
              <div className="text-[11px] text-muted-foreground mt-1">{s.t}</div>
            </div>
          ))}
        </div>

        <div className="eos-card p-0 overflow-hidden">
          <div className="p-4 border-b border-border flex items-center gap-2">
            <FileText size={16} weight="duotone"/>
            <div className="font-heading font-semibold text-base">All exams</div>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-secondary text-muted-foreground">
              <tr>{["Code", "Title", "Date", "Mode", "Students", "Status", "Actions"].map(h => (
                <th key={h} className="text-left px-4 py-2.5 text-[11px] uppercase tracking-[0.15em] font-medium">{h}</th>
              ))}</tr>
            </thead>
            <tbody>
              {exams.map(e => (
                <tr key={e.exam_id} className="border-t border-border hover:bg-secondary/40" data-testid={`exam-row-${e.exam_id}`}>
                  <td className="px-4 py-3 font-mono-eos text-[12px]">{e.code}</td>
                  <td className="px-4 py-3 font-medium">{e.title}</td>
                  <td className="px-4 py-3 text-muted-foreground">{e.date}</td>
                  <td className="px-4 py-3"><span className={`text-[11px] px-2 py-1 rounded-md ${MODE_CLS[e.mode] || "bg-secondary"}`}>{e.mode}</span></td>
                  <td className="px-4 py-3">{e.students}</td>
                  <td className="px-4 py-3"><span className={`text-[11px] px-2 py-1 rounded-md ${STATUS_CLS[e.status] || "bg-secondary"}`}>{e.status}</span></td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" className="h-7 px-2 text-xs rounded" data-testid={`evaluate-${e.exam_id}`}><Stamp size={12} className="mr-1"/> Evaluate</Button>
                      <Button variant="ghost" onClick={() => openEdit(e)} className="h-7 w-7 p-0 rounded" title="Edit" data-testid={`edit-${e.exam_id}`}><PencilSimple size={13}/></Button>
                      <Button variant="ghost" onClick={() => remove(e)} className="h-7 w-7 p-0 rounded text-rose-600 hover:text-rose-700 hover:bg-rose-50" title="Delete" data-testid={`delete-${e.exam_id}`}><Trash size={13}/></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <CrudModal
        open={modal.open}
        onClose={close}
        title={modal.editing ? "Edit exam" : "Create exam"}
        fields={FIELDS}
        initial={modal.editing || { mode: "Online", status: "Scheduled", students: 0 }}
        onSubmit={save}
        submitLabel={modal.editing ? "Save changes" : "Create exam"}
      />
    </div>
  );
}
